import { type FC, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { LAWYER_ROUTES } from "@/shared/constants/paths";
import { supabase } from "@/shared/lib/supabase";
import { useOnboardingAuth } from "@/shared/hooks/useOnboardingAuth";
import logoIcon from "@/shared/assets/logo/logo-icon.svg";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { saveEstateDetailsSafe } from "@/shared/utils/saveEstateDetails";
import { EventType, trackEvent } from "@/shared/utils/core";
import { getDecedentPath, getOnboardingFlow } from "@/shared/utils/funnelAnalytics";

export const AttorneyRedirect: FC<FlowCustomComponentProps> = ({ answers, onComplete }) => {
  const navigate = useNavigate();
  const { createPersonRecord } = useOnboardingAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoToAttorneys = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);

    const { error: invokeError } = await supabase.functions.invoke("account/convert-to-lawyer");

    if (invokeError) {
      setError(
        "Your account wasn't switched, and nothing you entered was lost. Try again, or chat with our team and we'll switch it for you.",
      );
      setIsLoading(false);
      return;
    }

    trackEvent(EventType.ATTORNEY_REDIRECT_ANSWERED, {
      flow: getOnboardingFlow(answers),
      attorney_choice: "attorney_portal",
      decedent_path: getDecedentPath(answers),
    });
    navigate(LAWYER_ROUTES.SIGNUP_PROFILE, { replace: true });
  };

  const handleContinueAsNok = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);

    const decedentName = (answers["decedent-name"] as string) || "";
    const middleName = (answers["scraped-middle-name"] as string) || undefined;
    const isManualPath = answers["has-obituary"] !== "yes";

    if (isManualPath && decedentName) {
      const personRecord = await createPersonRecord({ fullName: decedentName, middleName });

      if (!personRecord) {
        setError("We couldn't set up the estate. Try again.");
        setIsLoading(false);
        return;
      }

      const dob = (answers["decedent-dob"] as string) || undefined;
      const dod = (answers["decedent-dod"] as string) || undefined;
      const relationship = (answers["decedent-relationship"] as string) || "";

      await saveEstateDetailsSafe({ estateId: personRecord.estate_id, dob, dod, relationship });
    }

    trackEvent(EventType.ATTORNEY_REDIRECT_ANSWERED, {
      flow: getOnboardingFlow(answers),
      attorney_choice: "continue_as_family",
      decedent_path: getDecedentPath(answers),
    });
    setIsLoading(false);
    onComplete();
  };

  return (
    <div className="flex flex-col items-center gap-10">
      <img src={logoIcon} alt="Elayne" className="h-16 w-16" />

      <div className="flex w-full flex-col items-center gap-3 text-center">
        <h2>Settling estates for your clients?</h2>
        <p className="text-secondary">
          This account is for a family member settling one estate. Switch to Elayne for Attorneys,
          and all your client estates sit on one dashboard.
        </p>
      </div>

      <div className="flex w-full max-w-[400px] flex-col items-center gap-2">
        {error && <FieldError message={error} />}
        <Button
          variant="brand"
          rounded={true}
          onClick={handleGoToAttorneys}
          disabled={isLoading}
          className="w-full"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              Go to Elayne for Attorneys <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
        <Button variant="ghost" onClick={handleContinueAsNok} disabled={isLoading}>
          Settling a family estate yourself? Continue
        </Button>
      </div>
    </div>
  );
};
